import { Button, CardHeader, Typography, Chip } from "@material-ui/core";
import { onAuthStateChanged } from "firebase/auth";
import React, { useCallback, useEffect, useState } from "react";
import {
  Datagrid,
  ExportButton,
  FunctionField,
  List,
  NumberField,
  Pagination,
  ReferenceField,
  TextField,
  TopToolbar,
  useDataProvider,
} from "react-admin";
import { auth } from "../../firebase";
import TransactionFilter from "../Filters/TransactionFilter";
import { FilterList as FilterListIcon } from "@mui/icons-material";
import _extendLayout from "../../layout/_extendLayout";
import axios from "axios";
import { Table } from "react-bootstrap";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Paginations from "../Pagination";

const CustomActions = ({
  setShowTransactionFilter,
  proExporter,
  appliedFilters,
}) => {
  const getLabel = (key, value) => {
    if (key === "Status") {
      return `Status: ${value}`;
    }
    if (key === "Machine UUID") {
      return `Machine UUID: ${value}`;
    }
    if (key === "Date") {
      return `Date: ${value}`;
    }
    return `${key}: ${value}`;
  };

  return (
    <TopToolbar
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "center" }}></div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        {(appliedFilters || []).map((filter, index) =>
          filter?.value ? (
            <Chip
              key={index}
              label={getLabel(filter.key, filter.value)}
              color="primary"
            />
          ) : null
        )}
        <Button
          startIcon={<FilterListIcon />}
          variant="outlined"
          color="primary"
          style={{ border: "none" }}
          onClick={() => setShowTransactionFilter(true)}
        >
          Show Filters
        </Button>
        <Button
          color="primary"
          startIcon={<FileDownloadIcon />}
          onClick={proExporter}
        >
          Export
        </Button>
      </div>
    </TopToolbar>
  );
};

const TransactionProductList = ({ clientField = "client" }) => {
  const dataProvider = useDataProvider();

  const [isAdmin, setIsAdmin] = useState(false);
  const [proList, setProList] = useState([]);
  const [clientId, setClientId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mroSortField, setMroSortField] = useState("Date");
  const [mroSortOrder, setMroSortOrder] = useState("DESC");
  const [mroPage, setMroPage] = useState(1);
  const [mroFilters, setMroFilters] = useState({});
  const [perPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [showTransactionFilter, setShowTransactionFilter] = useState(false);

  const roundDecimal = (value) => Math.round(value * 100) / 100;

  useEffect(() => {
    const checkAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const { filter: currentFilter, admin } = await getCurrentUser(user.uid);
      }
    });

    return () => checkAuth();
  }, []);

  const getCurrentUser = async (userId) => {
    try {
      const res = await dataProvider.getOne("users", { id: userId });

      const admin = res.data.role === "admin";
      setIsAdmin(admin);

      let currentFilter = admin
        ? { mode: "mro" }
        : {
            client: res.data?.client?.id,
            mode: "mro",
            status: 1,
          };

      if (!admin) setClientId(res.data?.client?.id);

      return { filter: currentFilter, admin };
    } catch (err) {
      console.log(err);
    }
  };

  const getTransactionList = async (filter, pagination, sort) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      let skip = (pagination.page - 1) * pagination.perPage;
      let limit = pagination.perPage;
      let sortField = sort.field || "Date";
      let sortOrder = sort.order || "DESC";
      let params = {
        ...filter,
        skip,
        limit: 2000,
        sortField: sortField,
        sortOrder: sortOrder,
      };

      params.mode = "mro";

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroListByProduct`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const rawResult = res.data.result.data;
      const total = res.data.result.total.length;
      if (Array.isArray(rawResult)) {
        const grouped = groupByProduct(rawResult);
        const startIndex = (pagination.page - 1) * pagination.perPage;
        const endIndex = startIndex + pagination.perPage;
        const paginatedEmployees = grouped.slice(startIndex, endIndex);
        setFilteredData(paginatedEmployees);
        setGroupedData(grouped);
        setProList(grouped);
        setTotal(grouped.length);
      } else {
        console.error("Invalid data format:", rawResult);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const groupByProduct = (transactions) => {
    const grouped = transactions.reduce((acc, transaction) => {
      const productName = transaction.product?.name;
      if (!productName) return acc;

      if (!acc[productName]) {
        acc[productName] = {
          product: transaction.product,
          client: transaction.client,
          date: transaction.date,
          transactionId: transaction.transactionId,
          companyName: transaction.companyName,
          totalQty: 0,
          totalCredit: 0,
        };
      }

      const qty = Number(transaction.totalQty) || 0;
      const revenue = Number(transaction.totalRevenue) || 0;

      acc[productName].totalQty += qty;
      acc[productName].totalCredit += revenue;

      return acc;
    }, {});

    return Object.values(grouped).map((item) => ({
      ...item,
      totalCredit: Number(roundDecimal(item.totalCredit)),
    }));
  };

  const handleModalOpenChange = useCallback((isOpen) => {
    setIsModalOpen(isOpen);
  }, []);

  const proExporter = async (pro) => {
    const headers = [
      "ID",
      "Client",
      "Product Category ID",
      "Product Name",
      "Product Price",
      "Product Slot ID",
      "Total Quantity",
      "Total Transactions",
      "Total Cash",
      "Total Credit",
    ];

    let csvRows = [headers.join(",")];

    const groupedData = pro.reduce((acc, e) => {
      const productId = e.product?.id;
      if (!productId) return acc;

      if (!acc[productId]) {
        acc[productId] = {
          id: e.transactionId,
          client: e.client,
          product: e.product,
          totalQty: 0,
          totalCash: 0,
          totalCredit: 0,
          totalTransactions: 0,
        };
      }

      const { totalQty = 0, totalCredit = 0 } = e;

      acc[productId].totalQty += totalQty;
      acc[productId].totalTransactions += totalQty;
      acc[productId].totalCredit += totalCredit;

      return acc;
    }, {});

    Object.values(groupedData).forEach((e) => {
      const commonDetails = [
        e.id,
        e.client ?? "",
        e.product?.categoryId ?? "",
        e.product?.name ?? "",
        e.product?.price ?? "",
        e.product?.slot ?? "",
        e.totalQty, //total qty
        e.totalTransactions, //total transactions
        e.totalCash, //total cash
        e.totalCredit, //total credit
      ];
      csvRows.push([...commonDetails].join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "MRO Report Products.csv";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSortChange = (field) => {
    setMroSortField(field);
    setMroSortOrder((prevSortOrder) => {
      const newOrder = prevSortOrder === "ASC" ? "DESC" : "ASC";
      return newOrder;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      let filter = isAdmin
        ? { mode: "mro" }
        : { client: clientId, mode: "mro" };

      await getTransactionList(
        mroFilters,
        { page: mroPage, perPage: perPage },
        { field: mroSortField, order: mroSortOrder }
      );
    };

    fetchData();
  }, [mroPage, mroSortField, mroSortOrder]);

  return (
    <>
      <_extendLayout isModalOpen={isModalOpen || showTransactionFilter}>
        <div style={{ width: "90%" }}>
          <CardHeader title="MRO List By Product" />
          {loading ? (
            <Typography>Loading...</Typography>
          ) : groupedData.length == 0 ? (
            <Typography>No data found</Typography>
          ) : (
            <>
              <CustomActions
                proExporter={() => proExporter(filteredData)}
                setFilteredData={setFilteredData}
                setShowTransactionFilter={setShowTransactionFilter}
                data={proList}
              />
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th
                      className="fixed-col-product"
                      onClick={() => handleSortChange("Product.name")}
                      style={{ cursor: "pointer" }}
                    >
                      Product Name{" "}
                      {mroSortField === "Product.name" &&
                        (mroSortOrder === "ASC" ? "↑" : "↓")}
                    </th>
                    {isAdmin && <th>Company Name</th>}
                    <th
                      className="fixed-col-transaction"
                      onClick={() => handleSortChange("totalQty")}
                      style={{ cursor: "pointer" }}
                    >
                      Total QTY Sold{" "}
                      {mroSortField === "totalQty" &&
                        (mroSortOrder === "ASC" ? "↑" : "↓")}
                    </th>
                    <th
                      className="fixed-col-transaction"
                      onClick={() => handleSortChange("totalRevenue")}
                      style={{ cursor: "pointer" }}
                    >
                      Total Revenue Credit{" "}
                      {mroSortField === "totalRevenue" &&
                        (mroSortOrder === "ASC" ? "↑" : "↓")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isAdmin ? 12 : 11}
                        style={{ textAlign: "center", padding: "1rem" }}
                      >
                        No data found
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((item, index) => (
                      <tr key={item.transactionId}>
                        <td>{item.product.name}</td>
                        {isAdmin && <td>{item.companyName}</td>}
                        <td>{item.totalQty}</td>
                        <td>{item.totalCredit}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Paginations
                  total={total}
                  currentPage={mroPage}
                  perPage={perPage}
                  onPageChange={setMroPage}
                />
              </div>
            </>
          )}
        </div>

        {showTransactionFilter && (
          <TransactionFilter
            storageKey="transaction-product-filters"
            setFilteredData={setFilteredData}
            allData={proList}
            modalOpen={showTransactionFilter}
            onModalOpenChange={setShowTransactionFilter}
            setTotal={setTotal}
            setFilter={setMroFilters}
            setIsLoading={setLoading}
            sales={{ sales: false, module: "product" }}
          />
        )}
      </_extendLayout>
    </>
  );
};

export default TransactionProductList;
